import { useEffect, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  CheckCircle2, Info, ArrowUpRight, Star, Clock, MapPin,
  Utensils, Landmark, Palette, Leaf, Moon, Music, Building2, Heart, ShoppingBag, Sparkles, Globe,
  Sunrise, Sun, Sunset,
} from "lucide-react";
import { CATEGORY_META, TIME_META } from "@/lib/experiences/categoryMeta";
import { isUserFacingValidUrl } from "@/lib/experiences/linkIntegrity";
import { normalizeExperienceTitleForDisplay } from "@/lib/experiences/titleDisplay";
import { normalizeExperienceDescriptionForDisplay } from "@/lib/experiences/descriptionDisplay";
import { track } from "@/lib/analytics";
import { logMarketplaceEvent } from "@/lib/marketplaceEventLog";
import { SelectionAction } from "./SelectionAction";
import { getTrustedExperienceImage, getTrustedExperienceImageFallback, getTrustedExperienceImageAlt } from "./mediaRelevance";
import { getExcursionLabel } from "./marketplaceInventory";
import type { MarketplaceItem } from "./marketplaceInventory";
import type { MarketplaceDetailSurface } from "@/lib/analytics";
import { getProviderLinkState, getProviderSupportingText, getProviderContinueCta, formatProviderDisplayName, getPriceAvailabilityTrustNote } from "./providerLinkState";
import { buildProviderClickedProps } from "./detailAnalytics";
import { FOCUS_RING } from "./interactionStyles";
import { usePreferences } from "@/lib/hooks/usePreferences";
import { useFxRates } from "@/lib/hooks/useFxRates";
import { convertPriceForDisplay, formatConvertedPrice } from "@/lib/experiences/currencyConversion";
import { useI18n } from "@/lib/i18n/useI18n";
import { INTL_LOCALE_BY_LANGUAGE } from "@/lib/i18n/languages";

const ROW_CATEGORY_ICONS: Readonly<Record<string, LucideIcon>> = {
  food: Utensils, nightlife: Moon, culture: Landmark, art: Palette, nature: Leaf,
  music: Music, architecture: Building2, wellness: Heart, shopping: ShoppingBag, anime: Sparkles,
};
const ROW_TIME_ICONS: Readonly<Record<string, LucideIcon>> = {
  morning: Sunrise, afternoon: Sun, evening: Sunset, night: Moon,
};

// ─── Marketplace inventory row ─────────────────────────────────────────────────
// Flat, predictable list row for "Experiencias disponibles" — a
// marketplace listing, not a decorative itinerary card. Fields are grouped
// into distinct, low-noise rows (trust+provider, title, duration/time/category,
// location) so cards stay comparable at a glance instead of reading as one
// dense line.
export function ExperienceMarketplaceRow({
  item,
  cityHint,
  cityIata,
  rawQuery,
  isExcursion = false,
  onAdd,
  onRemove,
  onOpenDetail,
}: {
  item:         MarketplaceItem;
  cityHint?:    string | null;
  cityIata?:    string | null;
  /** Marketplace Media Relevance & Visual Trust V1 — the user's literal
   *  typed query, used only to keep the image decision consistent with the
   *  activity-intent conflict check that already gates the recommendation
   *  badge. Optional: a generic/empty query never changes the decision. */
  rawQuery?:    string | null;
  /** Marketplace Inventory Sections V1 — set by the caller from which
   *  group (see groupMarketplaceByIntent) this item belongs to. Renders a
   *  subtle "Excursión"/"Viaje de varios días" metadata segment, never a
   *  loud badge — the provider trust label stays unchanged. */
  isExcursion?: boolean;
  onAdd:        () => void;
  onRemove:     () => void;
  /** Marketplace Product Detail & Conversion V1 — opens ExperienceDetailDrawer
   *  for this item. Optional only so existing snapshot/tests that don't wire
   *  it don't crash; every real caller passes it. */
  onOpenDetail?: (item: MarketplaceItem, surface: MarketplaceDetailSurface) => void;
}) {
  const { exp, neighborhood, isSelected } = item;
  const { t, language } = useI18n();

  const isProvider = exp.source_type === "provider";
  const catMeta  = CATEGORY_META[exp.category] ?? { label: exp.category, color: "oklch(0.50 0.06 200)" };
  const CatIcon  = ROW_CATEGORY_ICONS[exp.category] ?? Globe;
  const timeMeta = TIME_META[exp.time_of_day] ?? TIME_META.any;
  const TimeIcon = ROW_TIME_ICONS[exp.time_of_day] ?? null;
  const hasSpecificTime = Boolean(exp.time_of_day) && exp.time_of_day !== "any";

  const hasDuration = typeof exp.duration_hours === "number" && exp.duration_hours > 0;
  const isTipBased  = exp.pricing_model === "tip_based";
  const hasPrice    = exp.price_from != null;
  const hasRating   = typeof exp.rating === "number" && exp.rating > 0;
  const hasReviewCount = typeof exp.review_count === "number" && exp.review_count > 0;
  const hasProviderName = isProvider && typeof exp.provider_name === "string" && exp.provider_name.trim() !== "";
  const providerDisplayName = hasProviderName ? formatProviderDisplayName(exp.provider_name) : null;

  // Real Data Policy: only ever surfaced from fields the backend actually set —
  // never inferred or invented when the underlying signal is absent.
  const isPriceOrientative = hasPrice && exp.catalog_only === true;
  const isAvailabilityUncertain = isProvider
    && (exp.availability_status === "skipped_no_date" || exp.availability_status === "error");
  const isAvailabilityConfirmed = isProvider && exp.availability_confirmed === true;

  // One quiet line instead of two competing labels — muted metadata, never a
  // badge that could visually outweigh the provider-verified trust signal.
  const uncertaintyNote = getPriceAvailabilityTrustNote(isPriceOrientative, isAvailabilityUncertain, language);

  // Reference Currency Display V1.1 — see ExperienceMarketplaceCard.tsx's
  // own comment for the full rationale (shared rates, no refetch on
  // currency change).
  const { currency: selectedCurrency } = usePreferences();
  const { data: fxRates } = useFxRates();
  const converted = convertPriceForDisplay({
    originalAmount: exp.price_from ?? null,
    originalCurrency: exp.currency ?? null,
    selectedCurrency,
    rates: fxRates?.rates,
    isTipBased,
  });

  const priceDisplay = isTipBased
    ? t("marketplace.price.tipBased")
    : hasPrice
      ? (exp.price_from === 0
          ? t("marketplace.price.free")
          : t("marketplace.price.from", { price: formatConvertedPrice(converted.amount ?? exp.price_from!, converted.currency) }))
      : null;
  const priceDisclaimer = converted.isReferenceConversion
    ? `${t("marketplace.price.disclaimer")}${
        fxRates?.as_of ? ` ${t("marketplace.price.disclaimerUpdated", { date: new Date(fxRates.as_of).toLocaleDateString(INTL_LOCALE_BY_LANGUAGE[language]) })}` : ""
      }`
    : undefined;

  const linkState = getProviderLinkState(exp);

  const heroSrc = getTrustedExperienceImage(exp, rawQuery, cityHint);
  const [imgSrc, setImgSrc] = useState<string | null>(heroSrc);
  useEffect(() => {
    setImgSrc(heroSrc);
  }, [heroSrc]);
  const isRealPhoto = imgSrc === exp.image_url && isUserFacingValidUrl(exp.image_url);
  const handleImgError = () => {
    const fallback = getTrustedExperienceImageFallback(exp, cityHint);
    if (imgSrc === exp.image_url && fallback) setImgSrc(fallback);
    else setImgSrc(null);
  };

  const description = normalizeExperienceDescriptionForDisplay(
    (exp.supplier_explanation && exp.supplier_explanation.length > 0)
      ? exp.supplier_explanation
      : (exp.editorial_note && exp.editorial_note.length > 0)
        ? exp.editorial_note
        : (exp.description || null)
  );
  // Card Quality V2: the primary CTA slot is the booking link whenever one is
  // valid, regardless of source_type — a provider item without a usable
  // booking_url shows the honest "Enlace no disponible" state instead (below).
  const showBookingCta = linkState === "bookable";
  // Provider Deep Links & Viator Images V1: a provider item without a valid
  // booking_url must say so honestly — never render an anchor with no real
  // destination.
  const showMissingProviderLink = linkState === "missing_link";
  const providerSupportingText = getProviderSupportingText(exp, language);

  const displayTitle = normalizeExperienceTitleForDisplay(exp.title, { isProvider, cityHint });

  const primaryMetaParts = [
    hasDuration ? `${exp.duration_hours}h` : null,
    hasSpecificTime ? timeMeta.label : null,
    catMeta.label,
    // Metadata-level, not a badge — sits in the same quiet fact row as
    // duration/time-of-day/category, never competes visually with the
    // provider trust label above.
    isExcursion ? getExcursionLabel(exp) : null,
  ].filter((s): s is string => Boolean(s));

  const locationLabel = neighborhood || null;

  // Truthful badge copy — mirrors FeaturedShelf's FeaturedCard: the generic
  // "Buen match" becomes the more specific real claim when time_of_day is
  // genuinely morning, never an inferred or decorative label.
  const badgeLabel = item.recommendationBadge === "top"
    ? "Recomendada por Sift Trip"
    : item.recommendationBadge === "good"
      ? (hasSpecificTime && exp.time_of_day === "morning" ? "Buena para la mañana" : "Buen match")
      : null;

  return (
    // Marketplace Interaction Polish V1, task 1 — a static list row reads
    // as inert; a faint background tint on hover (desktop pointer only —
    // no touch-device hover state to worry about) is the cheapest signal
    // that this row is interactive, without a shadow-lift or border change
    // that would compete with the divide-y list boundary the parent already
    // draws. No rounding here — the parent's overflow-hidden clips corners.
    <div className="flex flex-col sm:flex-row gap-3 p-3 sm:p-4 transition-colors duration-150 hover:bg-[oklch(0.960_0.010_72_/_0.5)] motion-reduce:transition-none">
      {/* Thumbnail — Launch Conversion UX V1, task 1/2: provider photos run
          larger than local/reference ones, a deliberate size hierarchy (not
          just copy) that makes real supplier inventory visually primary and
          local content visually secondary, matching the existing "Verificado
          con proveedor" vs "Referencia local" trust distinction below. */}
      <div
        className={`shrink-0 rounded-lg overflow-hidden w-full flex items-center justify-center ${
          isProvider ? "h-36 sm:w-[144px] sm:h-[108px]" : "h-28 sm:w-[100px] sm:h-[76px]"
        }`}
        style={{ background: "oklch(0.960 0.010 72)" }}
      >
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={getTrustedExperienceImageAlt(exp, rawQuery, displayTitle)}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={handleImgError}
          />
        ) : (
          <CatIcon size={26} aria-hidden style={{ color: catMeta.color, opacity: 0.4 }} />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {badgeLabel && (
              <p
                className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.08em] mb-1"
                style={{ color: item.recommendationBadge === "top" ? "#E8194B" : "#E8194B99" }}
              >
                <Star size={10} aria-hidden style={{ fill: "currentColor" }} />
                {badgeLabel}
              </p>
            )}

            {/* Trust row: source + (provider name, when known) */}
            <span
              className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.06em]"
              style={{ color: isProvider ? "oklch(0.46 0.12 145)" : "oklch(0.545 0.020 60 / 0.55)" }}
              title={providerDisplayName ?? undefined}
            >
              {isProvider ? <CheckCircle2 size={10} aria-hidden /> : <Info size={10} aria-hidden />}
              {isProvider ? "Verificado con proveedor" : "Referencia local"}
              {providerDisplayName && (
                <span className="normal-case font-semibold" style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}>
                  · {providerDisplayName}
                </span>
              )}
            </span>

            <h3
              className="font-display font-bold text-[15px] sm:text-[16px] leading-snug mt-0.5"
              style={{ color: "var(--color-heading-navy)" }}
            >
              {displayTitle}
            </h3>

            {/* Exact-match badge — Hotfix: Global Currency Formatting +
                Exact Query Match Boost + No-Transfers Enforcement V1,
                Parte B. Own line under the title, distinct from the
                trust row above and the price/rating column to the right
                — never competes with either. */}
            {exp.semantic_exact_query_match && (
              <p
                className="inline-flex items-center gap-1 text-[10px] font-semibold mt-0.5"
                style={{ color: "oklch(0.545 0.020 60 / 0.7)" }}
              >
                <Star size={10} aria-hidden style={{ fill: "#E8194B", color: "#E8194B" }} />
                {t("marketplace.badge.exactMatch")}
              </p>
            )}

            {/* Meta row 1 — comparable facts: duration · time-of-day · category */}
            <p
              className="text-[11px] mt-1 leading-snug flex items-center flex-wrap gap-x-1.5"
              style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}
            >
              {hasDuration && <Clock size={11} aria-hidden style={{ flexShrink: 0 }} />}
              {!hasDuration && TimeIcon && hasSpecificTime && <TimeIcon size={11} aria-hidden style={{ flexShrink: 0 }} />}
              {primaryMetaParts.join(" · ")}
            </p>

            {/* Meta row 2 — location and, when relevant, price/availability
                status merged into one quiet line, so the card reads as two
                clean meta rows instead of three competing ones. */}
            {(locationLabel || uncertaintyNote) && (
              <p
                className="text-[11px] mt-0.5 leading-snug flex items-center flex-wrap gap-x-1.5 gap-y-0.5"
                style={{ color: "oklch(0.545 0.020 60 / 0.42)" }}
              >
                {locationLabel && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={11} aria-hidden style={{ flexShrink: 0 }} />
                    {locationLabel}
                  </span>
                )}
                {locationLabel && uncertaintyNote && <span aria-hidden="true">·</span>}
                {uncertaintyNote && <span>{uncertaintyNote}</span>}
              </p>
            )}
          </div>

          {/* Desktop: price + rating/reviews + availability + selection
              action, right-aligned — every scannable decision signal lives
              in one column instead of splitting availability into the CTA
              row below. */}
          <div className="hidden sm:flex sm:flex-col sm:items-end gap-1.5 shrink-0">
            {priceDisplay && (
              <span className="text-[13px] font-bold" style={{ color: "oklch(0.120 0.040 258)" }} title={priceDisclaimer}>{priceDisplay}</span>
            )}
            {hasRating && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold" style={{ color: "#E8194B" }}>
                <Star size={10} aria-hidden style={{ fill: "currentColor" }} />
                {exp.rating!.toFixed(1)}
                {hasReviewCount && ` (${exp.review_count})`}
              </span>
            )}
            {isAvailabilityConfirmed && (
              <span className="text-[10px] font-medium" style={{ color: "oklch(0.42 0.12 256)" }}>
                Reservable ahora
              </span>
            )}
            <SelectionAction isSelected={isSelected} onAdd={onAdd} onRemove={onRemove} itemTitle={displayTitle} />
          </div>
        </div>

        {description && (
          <p
            className="text-[12px] leading-relaxed mt-1.5 line-clamp-2"
            style={{ color: "oklch(0.38 0.010 55 / 0.62)" }}
          >
            {description}
          </p>
        )}

        {/* Bottom row: CTA(s) on the left, mobile price/action on the right.
            Marketplace Product Detail & Conversion V1, task 4: provider rows
            get a primary booking CTA + secondary "Ver detalle" (opens the
            drawer); local rows get "Ver detalle" as their primary action —
            there is no booking CTA to compete with it. Never a link-styled
            wrapper around the whole row: SelectionAction stays a separate
            control either way. */}
        <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
          <div className="flex items-center gap-2.5 flex-wrap">
            {showBookingCta && (
              <span className="inline-flex items-center gap-1.5 flex-wrap">
                <a
                  href={exp.booking_url!}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  onClick={() => {
                    track("booking_clicked", {
                      provider_name: exp.provider_name ?? undefined,
                      city:          exp.neighborhood ?? "",
                      price_from:    exp.price_from ?? undefined,
                    });
                    track("provider_product_clicked", {
                      experience_id:                exp.id,
                      iata:                         cityIata ?? undefined,
                      source_type:                  exp.source_type ?? null,
                      provider_name:                exp.provider_name ?? null,
                      marketplace_relevance_class:  exp.marketplace_relevance_class ?? null,
                      surface:                      "marketplace_list",
                    });
                    // Launch Analytics & Funnel Instrumentation V1 — canonical
                    // funnel event, fired alongside the two legacy ones above.
                    const _clickProps = buildProviderClickedProps(exp, cityIata, "marketplace_list");
                    track("marketplace_provider_clicked", _clickProps);
                    // Admin Metrics UX V1 — same event, alongside PostHog.
                    logMarketplaceEvent("marketplace_provider_clicked", {
                      destination_iata: _clickProps.destination_iata,
                      provider_name:    _clickProps.provider_name,
                      category:         _clickProps.category,
                      surface:          _clickProps.surface,
                      provider_clicked: true,
                      has_booking_url:  _clickProps.has_booking_url,
                      link_variant:     _clickProps.link_variant,
                    });
                  }}
                  className={`inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-lg transition-all duration-150 hover:opacity-85 active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 ${FOCUS_RING}`}
                  style={{ color: "oklch(0.998 0.003 72)", background: "oklch(0.120 0.040 258)" }}
                >
                  {getProviderContinueCta(exp, language)}
                  <ArrowUpRight size={11} aria-hidden />
                </a>
                <span className="text-[10px]" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
                  {providerSupportingText}
                </span>
              </span>
            )}
            {showMissingProviderLink && (
              <span className="inline-flex items-center gap-1.5 flex-wrap">
                <span
                  aria-disabled="true"
                  className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-lg"
                  style={{ color: "oklch(0.545 0.020 60 / 0.55)", background: "oklch(0.545 0.020 60 / 0.08)", cursor: "not-allowed" }}
                >
                  {t("compare.linkUnavailable")}
                </span>
                <span className="text-[10px]" style={{ color: "oklch(0.545 0.020 60 / 0.45)" }}>
                  {t("marketplace.provider.noDirectLinkYet")}
                </span>
              </span>
            )}
            {onOpenDetail && (
              <button
                type="button"
                onClick={() => onOpenDetail(item, "marketplace_list")}
                aria-label={`Ver detalle de "${displayTitle}"`}
                className={`inline-flex items-center gap-1 rounded-lg transition-all duration-150 hover:opacity-80 active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 ${FOCUS_RING}`}
                style={isProvider
                  ? {
                      fontSize:   "11px",
                      fontWeight: 600,
                      color:      "oklch(0.545 0.020 60 / 0.65)",
                      background: "none",
                      border:     "none",
                      padding:    0,
                      cursor:     "pointer",
                    }
                  : {
                      fontSize:   "11px",
                      fontWeight: 700,
                      color:      "var(--color-heading-navy)",
                      background: "oklch(0.960 0.010 72)",
                      border:     "1px solid oklch(0.880 0.024 72 / 0.6)",
                      padding:    "6px 12px",
                      cursor:     "pointer",
                    }}
              >
                Ver detalle
              </button>
            )}
          </div>

          {/* Mobile: rating + price + selection action (desktop column is
              hidden here) — rating now shown on mobile too (Launch
              Conversion UX V1, task 1: rating must be scannable at every
              breakpoint, not desktop-only). */}
          <div className="flex sm:hidden items-center gap-2 ml-auto flex-wrap justify-end">
            {isAvailabilityConfirmed && (
              <span className="text-[10px] font-medium" style={{ color: "oklch(0.42 0.12 256)" }}>
                Reservable ahora
              </span>
            )}
            {hasRating && (
              <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold" style={{ color: "#E8194B" }}>
                <Star size={10} aria-hidden style={{ fill: "currentColor" }} />
                {exp.rating!.toFixed(1)}
                {hasReviewCount && ` (${exp.review_count})`}
              </span>
            )}
            {priceDisplay && (
              <span className="text-[12px] font-bold" style={{ color: "oklch(0.120 0.040 258)" }} title={priceDisclaimer}>{priceDisplay}</span>
            )}
            <SelectionAction isSelected={isSelected} onAdd={onAdd} onRemove={onRemove} itemTitle={displayTitle} />
          </div>
        </div>
      </div>
    </div>
  );
}
